import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationAPI } from '../api';
import OnboardingTutorial from './OnboardingTutorial';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { 
  LayoutDashboard, Calendar, FileText, Award, CheckSquare, 
  Settings, Menu, LogOut, ChevronDown, Bell, HelpCircle,
  Users, Crown, Boxes, Layers, Globe2, Library, Shield,
  Check, CheckCheck, Home, ListTodo
} from 'lucide-react';

const SECRETARIAT_ROLES = ['corporate_secretary', 'secretariat_staff', 'super_admin'];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const isLandlord = user?.role === 'landlord';
  const isSecretariat = isLandlord || SECRETARIAT_ROLES.includes(user?.role);

  useEffect(() => {
    if (user) {
      const done = localStorage.getItem('lexagenda_onboarding_done');
      if (!done) setShowOnboarding(true);
    }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationAPI.getAll(),
        notificationAPI.getUnreadCount(),
      ]);
      setNotifications(notifRes.data || []);
      setUnreadCount(countRes.data?.count || 0);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      const token = localStorage.getItem('token');
      if (token) {
        const wsBase = process.env.REACT_APP_BACKEND_URL
          ?.replace('https://', 'wss://')
          ?.replace('http://', 'ws://');
        const wsUrl = `${wsBase}/api/notifications/ws?token=${token}`;
        let ws;
        try {
          ws = new WebSocket(wsUrl);
          ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.type === 'new_notification') {
              setNotifications(prev => [data.notification, ...prev]);
              setUnreadCount(prev => prev + 1);
            } else if (data.type === 'unread_count') {
              setUnreadCount(data.count);
            }
          };
          ws.onclose = () => {};
          const pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send('ping');
          }, 25000);
          return () => {
            clearInterval(interval);
            clearInterval(pingInterval);
            if (ws.readyState === WebSocket.OPEN) ws.close();
          };
        } catch (e) { /* fallback to polling */ }
      }
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  const handleMarkRead = async (id) => {
    try { await notificationAPI.markRead(id); fetchNotifications(); } catch (err) { /* silent */ }
  };
  const handleMarkAllRead = async () => {
    try { await notificationAPI.markAllRead(); fetchNotifications(); } catch (err) { /* silent */ }
  };
  const handleLogout = () => { logout(); navigate('/login'); };

  // --- Top nav: Secretariat working tools ---
  const topNav = [
    { name: 'Governance Hub', href: '/governance', icon: Globe2 },
    { name: 'Meetings', href: '/meetings', icon: Calendar },
    { name: 'Tasks', href: '/action-items', icon: ListTodo },
    { name: 'Meeting Records', icon: FileText, children: [
      { name: 'Minutes', href: '/minutes', icon: FileText },
      { name: 'Resolutions', href: '/resolutions', icon: Award },
      { name: 'Templates', href: '/templates', icon: Layers },
    ]},
  ];

  // --- Bottom nav ---
  const bottomNavSecretariat = [
    { name: 'Home', href: '/dashboard', icon: Home },
    { name: 'Library', href: '/records', icon: Library },
    { name: 'Stakeholders', href: '/stakeholders', icon: Users, badge: true },
    { name: 'Helpdesk', href: '/help', icon: HelpCircle },
    ...(!isLandlord ? [{ name: 'Subscription', href: '/subscription', icon: Boxes }] : []),
  ];

  const bottomNavStakeholder = [
    { name: 'Home', href: '/dashboard', icon: Home },
    { name: 'Library', href: '/records', icon: Library },
    { name: 'Stakeholders', href: '/stakeholders', icon: Users, badge: true },
    { name: 'Helpdesk', href: '/help', icon: HelpCircle },
    { name: 'Subscription', href: '/subscription', icon: Boxes },
  ];

  const bottomNav = isSecretariat ? bottomNavSecretariat : bottomNavStakeholder;

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  const isGroupActive = (item) => {
    if (item.children) return item.children.some(c => isActive(c.href));
    return isActive(item.href);
  };

  // --- Notification dropdown (shared between both roles) ---
  const NotificationDropdown = () => (
    <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-[#5A6570] hover:text-[#1A1D21]" data-testid="notifications-btn">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[400px] overflow-y-auto">
        <div className="px-3 py-2 flex items-center justify-between border-b border-[#E2E6EA]">
          <p className="font-semibold text-sm text-[#1A1D21]">Notifications</p>
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead} className="text-xs text-[#00A3A3] hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
              <CheckCheck className="h-3 w-3" />Mark all read
            </button>
          )}
        </div>
        {notifications.length === 0 ? (
          <div className="py-8 text-center">
            <Bell className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
            <p className="text-sm text-[#5A6570]">No notifications yet</p>
          </div>
        ) : (
          notifications.slice(0, 20).map((n) => {
            const isUnread = !n.read_by?.includes(user?.id);
            return (
              <DropdownMenuItem key={n.id}
                className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer ${isUnread ? 'bg-[#00A3A3]/5' : ''}`}
                onClick={() => { if (isUnread) handleMarkRead(n.id); if (n.link) { navigate(n.link); setNotifOpen(false); } }}
                data-testid={`notification-item-${n.id}`}>
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${isUnread ? 'bg-[#00A3A3]' : 'bg-transparent'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${isUnread ? 'font-semibold text-[#1A1D21]' : 'text-[#5A6570]'}`}>{n.title}</p>
                  <p className="text-xs text-[#A8B1B9] mt-0.5 line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-[#A8B1B9] mt-1">
                    {n.created_at ? new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex flex-col">
      {/* ======= TOP NAVIGATION BAR ======= */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E2E6EA] shadow-sm">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden"><Menu className="h-6 w-6" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-[#E2E6EA]">
                  <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                    <img src="/logo-icon.png" alt="LexAgenda" className="h-10" />
                    <span className="text-lg font-bold">
                      <span className="text-[#00A3A3]">LEX</span><span className="text-[#F58A26]">AGENDA</span>
                    </span>
                  </Link>
                </div>
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                  {isSecretariat && (
                    <>
                      <p className="text-[10px] font-semibold text-[#A8B1B9] uppercase tracking-wider px-3 mb-2">Work</p>
                      {topNav.map((item) =>
                        item.children ? (
                          <div key={item.name} className="space-y-0.5">
                            <div className={`flex items-center gap-2 px-3 py-2 text-sm font-medium ${isGroupActive(item) ? 'text-[#00A3A3]' : 'text-[#5A6570]'}`}>
                              <item.icon className="h-4 w-4" /><span>{item.name}</span>
                            </div>
                            <div className="ml-6 space-y-0.5">
                              {item.children.map(child => (
                                <Link key={child.name} to={child.href} onClick={() => setMobileMenuOpen(false)}
                                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(child.href) ? 'bg-[#00A3A3] text-white' : 'text-[#5A6570] hover:bg-[#F0F2F4]'}`}>
                                  <child.icon className="h-4 w-4" />{child.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <Link key={item.name} to={item.href} onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(item.href) ? 'bg-[#00A3A3] text-white' : 'text-[#5A6570] hover:bg-[#F0F2F4]'}`}>
                            <item.icon className="h-4 w-4" />{item.name}
                          </Link>
                        )
                      )}
                      <div className="my-3 border-t border-[#E2E6EA]" />
                    </>
                  )}
                  <p className="text-[10px] font-semibold text-[#A8B1B9] uppercase tracking-wider px-3 mb-2">General</p>
                  {bottomNav.map(item => (
                    <Link key={item.name} to={item.href} onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(item.href) ? 'bg-[#00A3A3] text-white' : 'text-[#5A6570] hover:bg-[#F0F2F4]'}`}>
                      <item.icon className="h-4 w-4" />{item.name}
                    </Link>
                  ))}
                  {isLandlord && (
                    <>
                      <div className="my-3 border-t border-[#E2E6EA]" />
                      <Link to="/landlord-dashboard" onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive('/landlord-dashboard') ? 'bg-[#00A3A3] text-white' : 'text-[#5A6570] hover:bg-[#F0F2F4]'}`}>
                        <Crown className="h-4 w-4" />Landlord Admin
                      </Link>
                    </>
                  )}
                </nav>
              </div>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <img src="/logo-icon.png" alt="LexAgenda" className="h-11" />
            <span className="hidden xl:block text-lg font-bold">
              <span className="text-[#00A3A3]">LEX</span><span className="text-[#F58A26]">AGENDA</span>
            </span>
          </Link>

          {/* Desktop Top Navigation — Secretariat only */}
          {isSecretariat && (
            <nav className="hidden md:flex items-center gap-1" data-testid="top-nav">
              {topNav.map((item) =>
                item.children ? (
                  <DropdownMenu key={item.name}>
                    <DropdownMenuTrigger asChild>
                      <button className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isGroupActive(item) ? 'bg-[#00A3A3]/10 text-[#00A3A3]' : 'text-[#5A6570] hover:bg-[#F0F2F4] hover:text-[#1A1D21]'}`}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
                        <item.icon className="h-4 w-4" />
                        <span className="hidden lg:inline">{item.name}</span>
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {item.children.map(child => (
                        <DropdownMenuItem key={child.name} onClick={() => navigate(child.href)}
                          className={isActive(child.href) ? 'bg-[#00A3A3]/10 text-[#00A3A3]' : ''}
                          data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}>
                          <child.icon className="h-4 w-4 mr-2" />{child.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link key={item.name} to={item.href}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(item.href) ? 'bg-[#00A3A3]/10 text-[#00A3A3]' : 'text-[#5A6570] hover:bg-[#F0F2F4] hover:text-[#1A1D21]'}`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    <item.icon className="h-4 w-4" />
                    <span className="hidden lg:inline">{item.name}</span>
                  </Link>
                )
              )}
            </nav>
          )}

          {/* Right Section: Icons */}
          <div className="flex items-center gap-1">
            <NotificationDropdown />
            {isSecretariat && (
              <Link to="/admin">
                <Button variant="ghost" size="icon" className={isActive('/admin') ? 'text-[#00A3A3]' : 'text-[#5A6570]'} data-testid="settings-btn" title="Secretariat Settings">
                  <Settings className="h-5 w-5" />
                </Button>
              </Link>
            )}
            <Button variant="ghost" size="icon" className="text-[#5A6570] hover:text-red-600" onClick={handleLogout} data-testid="logout-btn" title="Sign Out">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* ======= MAIN CONTENT ======= */}
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full overflow-x-hidden pt-20 pb-24 md:pt-20 md:pb-24">
        {children}
      </main>

      {/* ======= BOTTOM NAVIGATION BAR ======= */}
      <nav className="sticky bottom-0 z-40 bg-gradient-to-r from-[#007A7A] to-[#00A3A3] shadow-[0_-4px_16px_rgba(0,0,0,0.12)] hidden md:block" data-testid="bottom-nav">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 h-[72px] px-6">
          {bottomNav.map(item => (
            <Link key={item.name} to={item.href}
              className={`relative flex flex-col items-center justify-center gap-1 px-5 py-2 rounded-xl transition-all min-w-[80px] ${
                isActive(item.href)
                  ? 'bg-white/20 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`bottom-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <item.icon className="h-6 w-6" strokeWidth={isActive(item.href) ? 2.5 : 2} />
              <span className="text-xs font-bold">{item.name}</span>
              {item.badge && unreadCount > 0 && (
                <span className="absolute top-1 right-2 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 shadow-sm">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          ))}
          {isLandlord && (
            <Link to="/landlord-dashboard"
              className={`flex flex-col items-center justify-center gap-1 px-5 py-2 rounded-xl transition-all min-w-[80px] ${isActive('/landlord-dashboard') ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
              data-testid="bottom-nav-landlord-admin">
              <Crown className="h-6 w-6" strokeWidth={isActive('/landlord-dashboard') ? 2.5 : 2} />
              <span className="text-xs font-bold">Admin</span>
            </Link>
          )}
        </div>
      </nav>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#007A7A] to-[#00A3A3] shadow-[0_-4px_16px_rgba(0,0,0,0.12)] md:hidden" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-[68px] px-1">
          {bottomNav.slice(0, 5).map(item => (
            <Link key={item.name} to={item.href}
              className={`relative flex flex-col items-center justify-center gap-1 px-2 py-1.5 rounded-xl min-w-0 ${
                isActive(item.href) ? 'text-white bg-white/20' : 'text-white/70'
              }`}
              data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <item.icon className="h-5 w-5" strokeWidth={isActive(item.href) ? 2.5 : 2} />
              <span className="text-[10px] font-bold truncate max-w-[72px]">{item.name}</span>
              {item.badge && unreadCount > 0 && (
                <span className="absolute top-0 right-0 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-0.5">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
          ))}
          {isLandlord && (
            <Link to="/landlord-dashboard"
              className={`relative flex flex-col items-center justify-center gap-1 px-2 py-1.5 rounded-xl min-w-0 ${
                isActive('/landlord-dashboard') ? 'text-white bg-white/20' : 'text-white/70'
              }`}
              data-testid="mobile-nav-landlord-admin">
              <Crown className="h-5 w-5" strokeWidth={isActive('/landlord-dashboard') ? 2.5 : 2} />
              <span className="text-[10px] font-bold truncate max-w-[72px]">Admin</span>
            </Link>
          )}
        </div>
      </nav>

      {/* Onboarding Tutorial */}
      {showOnboarding && (
        <OnboardingTutorial onComplete={() => setShowOnboarding(false)} />
      )}
    </div>
  );
};

export default Layout;
